import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, finalize, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

type HttpMethod = 'get' | 'post' | 'put' | 'delete';
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private loadingCount: number = 0;
  private BASE_URL: string = environment.apiUrl;
  public loading: boolean = false;
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(localStorage.getItem('accessToken') ? true : false);

  constructor(private router: Router, private httpClient: HttpClient) {}

  setBaseUrl(baseUrl: string) {
    this.BASE_URL = baseUrl;
  }

  getAccount() {
    return window.location.host.split(':')[0].split('.')[0];
  }

  get<T>(endpoint: string, isSilent: boolean = false): Observable<T> {
    return this.request('get', endpoint, {}, isSilent);
  }

  getById<T>(endpoint: string, id: string, isSilent: boolean = false): Observable<T> {
    if (!id) {
      return throwError(() => new Error('ID is required'));
    }
    const fullEndpoint = `${endpoint}/${id}`;
    return this.request('get', fullEndpoint, {}, isSilent);
  }

  post<T>(endpoint: string, body: any, isSilent: boolean = false): Observable<T> {
    return this.request('post', endpoint, body, isSilent);
  }

  put<T>(endpoint: string, body: any, isSilent: boolean = false): Observable<T> {
    return this.request('put', endpoint, body, isSilent);
  }

  delete<T>(endpoint: string, isSilent: boolean = false): Observable<T> {
    return this.request('delete', endpoint, {}, isSilent);
  }

  request<T>(method: HttpMethod, endpoint: string, body: any, isSilent: boolean = false): Observable<T> {
    if (!isSilent) {
      this.presentLoading();
    }
    const hasBody = method === 'post' || method === 'put';
    let response: Observable<any> | undefined;
    if (endpoint.indexOf('://') === -1) endpoint = `${this.BASE_URL}${endpoint}`;

    let options = {};
    if (endpoint.indexOf(this.BASE_URL) === 0 || endpoint.indexOf(environment.apiUrl) === 0) {
      options = {
        // withCredentials: true,
        headers: { 'x-app-role': 'admin', Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      };
    }
    // Usar switch para evitar a indexação
    switch (method) {
      case 'post':
        response = this.httpClient.post<T>(`${endpoint}`, body, options);
        break;
      case 'put':
        response = this.httpClient.put<T>(`${endpoint}`, body, options);
        break;
      case 'get':
        response = this.httpClient.get<T>(`${endpoint}`, options);
        break;
      case 'delete':
        response = this.httpClient.delete<T>(`${endpoint}`, options);
        break;
      default:
        throw new Error('Invalid HTTP method');
    }

    return response.pipe(
      catchError(async (err) => {
        console.error(err);
        const currentToken = localStorage.getItem('accessToken');
        if (this.isAuthenticated$.value && currentToken && err.error.statusCode == 401) {
          if (err.error.message == 'Unauthorized') {
            this.logout();
          } else if (err.error.message == 'Token expired') {
            this.logout(true);
          }
        }
        return err;
      }),
      finalize(() => {
        if (!isSilent) {
          this.dismissLoading();
        }
      }),
    );
  }

  private presentLoading() {
    this.loadingCount++;
    if (this.loadingCount >= 1) {
      this.loading = true;
      this.loading$.next(true);
    }
  }

  private dismissLoading() {
    this.loadingCount--;
    if (this.loadingCount <= 0 && this.loading) {
      this.loading = false;
      this.loading$.next(false);
    }
  }

  logout(redirect: boolean | string = false, redirectUrl: string = '/home') {
    localStorage.clear();
    this.isAuthenticated$.next(false);

    if (redirect) this.router.navigate([redirectUrl]);
  }
}
